import {BUILD_VERSION} from './buildInfo.js'
import {forceRefreshApp} from './forceRefresh.js'
import {logRuntimeEvent, resolvePendingFormSubmission} from './telemetry.js'
import {getRuntimeMode} from './appRuntime.js'

const REQUEST_TIMEOUT_MS = 15_000
const RETRYABLE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS'])
const RETRY_STATUS_CODES = new Set([408, 425, 429, 500, 502, 503, 504])

function createTimeoutError(url, timeoutMs) {
  const error = new Error(`Request timed out after ${timeoutMs}ms`)
  error.name = 'AbortError'
  error.code = 'REQUEST_TIMEOUT'
  error.url = url
  return error
}

function combineSignals(primary, secondary) {
  if (!primary) return secondary || null
  if (!secondary) return primary

  const controller = new AbortController()
  const abort = () => controller.abort(primary.reason || secondary.reason)

  if (primary.aborted || secondary.aborted) {
    abort()
  } else {
    primary.addEventListener('abort', abort, {once: true})
    secondary.addEventListener('abort', abort, {once: true})
  }

  return controller.signal
}

function getHeaderValue(headers, key) {
  if (!headers) return ''
  if (typeof headers.get === 'function') {
    return headers.get(key) || ''
  }
  if (Array.isArray(headers)) {
    const found = headers.find(([entryKey]) => String(entryKey).toLowerCase() === key.toLowerCase())
    return found?.[1] || ''
  }
  return headers[key] || headers[key.toLowerCase()] || ''
}

function setHeader(headers, key, value) {
  if (!value) return

  if (headers instanceof Headers) {
    headers.set(key, value)
    return
  }

  if (Array.isArray(headers)) {
    const index = headers.findIndex(([entryKey]) => String(entryKey).toLowerCase() === key.toLowerCase())
    if (index >= 0) {
      headers[index] = [key, value]
    } else {
      headers.push([key, value])
    }
    return
  }

  headers[key] = value
}

function shouldBypass(input, init) {
  const url = typeof input === 'string' ? input : input?.url || ''
  if (url.includes('/v2/telemetry')) {
    return true
  }
  return getHeaderValue(init?.headers || input?.headers, 'X-Bypass-App-Fetch') === '1'
}

function shouldRetry(method, headers, response) {
  const normalizedMethod = String(method || 'GET').toUpperCase()
  if (RETRYABLE_METHODS.has(normalizedMethod)) {
    return !response || RETRY_STATUS_CODES.has(response.status)
  }

  return Boolean(
    getHeaderValue(headers, 'X-Idempotency-Key') ||
    getHeaderValue(headers, 'X-Retry-Safe') === 'true'
  ) && (!response || RETRY_STATUS_CODES.has(response.status))
}

function shouldForceReload(response) {
  const reloadRequired = response.headers.get('x-app-reload-required')
  const versionStatus = response.headers.get('x-app-version-status')
  return reloadRequired === 'true' || versionStatus === 'mismatch'
}

async function runAttempt(originalFetch, input, init, timeoutMs) {
  const timeoutController = new AbortController()
  const url = typeof input === 'string' ? input : input?.url
  const timeoutId = window.setTimeout(() => timeoutController.abort(createTimeoutError(url, timeoutMs)), timeoutMs)

  try {
    const mergedSignal = combineSignals(init.signal, timeoutController.signal)
    return await originalFetch(input, {...init, signal: mergedSignal})
  } catch (error) {
    if (timeoutController.signal.aborted && !init.signal?.aborted) {
      throw createTimeoutError(url, timeoutMs)
    }
    throw error
  } finally {
    window.clearTimeout(timeoutId)
  }
}

export function installAppFetchPatch() {
  if (typeof window === 'undefined' || window.__APP_FETCH_PATCHED__) {
    return
  }

  const originalFetch = window.fetch.bind(window)
  window.__APP_ORIGINAL_FETCH__ = originalFetch

  window.fetch = async (input, init = {}) => {
    if (shouldBypass(input, init)) {
      return originalFetch(input, init)
    }

    const url = typeof input === 'string' ? input : input?.url || ''
    const method = String(init.method || input?.method || 'GET').toUpperCase()
    const headers = new Headers(init.headers || input?.headers || {})
    setHeader(headers, 'X-Client-Build-Version', BUILD_VERSION)
    setHeader(headers, 'X-App-Runtime-Mode', getRuntimeMode())

    const requestId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
    const timeoutMs = REQUEST_TIMEOUT_MS
    const requestInit = {...init, method, headers}

    let attempt = 0
    const maxAttempts = shouldRetry(method, headers) ? 2 : 1

    while (attempt < maxAttempts) {
      attempt += 1
      const startedAt = performance.now()
      logRuntimeEvent('request_started', {url, method, requestId, attempt, timeoutMs}, {flush: false})

      try {
        const response = await runAttempt(originalFetch, input, requestInit, timeoutMs)
        const durationMs = Math.round(performance.now() - startedAt)

        if (shouldForceReload(response)) {
          logRuntimeEvent('version_mismatch_detected', {
            url,
            method,
            requestId,
            attempt,
            serverBuildVersion: response.headers.get('x-frontend-build-version') || null
          })
          void forceRefreshApp({reason: 'version-mismatch'})
        }

        if (response.ok || attempt >= maxAttempts || !shouldRetry(method, headers, response)) {
          logRuntimeEvent(response.ok ? 'request_success' : 'request_failed', {
            url,
            method,
            requestId,
            attempt,
            status: response.status,
            durationMs
          }, {flush: response.ok})

          if (method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS') {
            resolvePendingFormSubmission({
              outcome: response.ok ? 'success' : 'failed',
              url,
              method,
              status: response.status,
              message: response.ok ? null : `HTTP ${response.status}`,
            })
          }

          return response
        }

        logRuntimeEvent('request_retry', {
          url,
          method,
          requestId,
          attempt,
          status: response.status
        }, {flush: false})
      } catch (error) {
        const durationMs = Math.round(performance.now() - startedAt)
        const isTimeout = error?.code === 'REQUEST_TIMEOUT'
        const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false

        logRuntimeEvent(isTimeout ? 'request_timeout' : 'request_failed', {
          url,
          method,
          requestId,
          attempt,
          durationMs,
          offline: isOffline,
          message: error?.message || 'Request failed'
        }, {flush: attempt >= maxAttempts})

        if (attempt >= maxAttempts && method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS') {
          resolvePendingFormSubmission({
            outcome: 'failed',
            url,
            method,
            status: error?.status || null,
            message: error?.message || 'Request failed',
          })
        }

        if (attempt >= maxAttempts || !shouldRetry(method, headers) || isOffline) {
          throw error
        }
      }

      await new Promise(resolve => window.setTimeout(resolve, 400 * attempt))
    }

    return originalFetch(input, requestInit)
  }

  window.__APP_FETCH_PATCHED__ = true
}
